import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const hysterosonography: ContentBlock<"procedure"> = {
  id: makeId("proc-hysterosonography"),
  kind: "procedure",
  tags: ["testing", "her", "clinic-visit"],
  title: "Hysterosonography",
  subtitle: "Saline infusion sonography to evaluate the uterine cavity",
  body: {
    procedureName: "Hysterosonography",
    category: "diagnostic",
    description:
      "Also called saline infusion sonography (SIS), this procedure uses sterile saline injected into the uterus during a transvaginal ultrasound to provide detailed images of the uterine cavity and identify polyps, fibroids, or other abnormalities.",
    howItWorks:
      "You lie on an exam table with your feet in stirrups. A thin catheter is passed through the cervix into the uterus, and a small amount of sterile saline is slowly infused. The saline expands the uterine cavity, allowing the transvaginal ultrasound probe to capture clear images of the inner walls. The doctor looks for polyps, submucosal fibroids, adhesions, or structural irregularities that could affect implantation. The entire process is done in real time on screen.",
    duration: "15–30 minutes",
    anesthesia: "none",
    whatToExpect: [
      "Cramping similar to menstrual cramps when saline is infused, usually mild and brief",
      "The procedure is performed in the first half of your cycle (after your period, before ovulation)",
      "You may experience light spotting or watery discharge afterward",
      "Results are typically discussed immediately",
      "You can return to normal activities right away",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "Findings are often discussed during the exam because the uterine cavity is viewed in real time.",
      },
      {
        label: "What you learn",
        detail:
          "The exam looks for polyps, fibroids, adhesions, or cavity shape issues that could affect implantation.",
      },
      {
        label: "Follow-up",
        detail:
          "If something is seen, your doctor may recommend hysteroscopy or another treatment before transfer.",
      },
    ],
    prepInstructions: [
      "Schedule during the first half of your menstrual cycle (days 5–12)",
      "Take an over-the-counter pain reliever (like ibuprofen) 30–60 minutes before if your doctor recommends it",
      "Avoid intercourse for 24 hours before the procedure to reduce infection risk",
      "You may be prescribed a short course of antibiotics beforehand",
    ],
    risks: [
      "Mild to moderate cramping during and shortly after the procedure",
      "Light spotting or watery discharge for 1–2 days",
      "Very rare: infection",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Hystérosonographie",
      description:
        "Aussi appelée hystérosonographie au sérum physiologique, cette procédure consiste à injecter du sérum salé stérile dans l'utérus pendant une échographie endovaginale pour obtenir des images détaillées de la cavité utérine et identifier polypes, fibromes ou autres anomalies.",
      howItWorks:
        "Vous êtes allongée sur la table d'examen, les pieds dans des étriers. Un fin cathéter est passé à travers le col jusque dans l'utérus, et une petite quantité de sérum physiologique stérile est injectée lentement. Le sérum dilate la cavité utérine, permettant à la sonde endovaginale de capter des images nettes des parois internes. Le médecin recherche des polypes, des fibromes sous-muqueux, des adhérences ou des anomalies structurelles pouvant gêner l'implantation. L'ensemble se fait en temps réel à l'écran.",
      duration: "15 à 30 minutes",
      whatToExpect: [
        "Crampes semblables à celles des règles lors de l'injection du sérum, généralement légères et brèves",
        "Examen réalisé dans la première moitié du cycle (après les règles, avant l'ovulation)",
        "Léger spotting ou écoulement aqueux possible ensuite",
        "Résultats généralement discutés immédiatement",
        "Reprise normale des activités tout de suite après",
      ],
      results: [
        {
          label: "Quand vous avez des nouvelles",
          detail:
            "Les résultats sont souvent discutés pendant l'examen, car la cavité utérine est vue en temps réel.",
        },
        {
          label: "Ce que vous apprenez",
          detail:
            "L'examen recherche polypes, fibromes, adhérences ou anomalies de forme de la cavité pouvant influencer l'implantation.",
        },
        {
          label: "Suivi",
          detail:
            "Si une anomalie est vue, votre médecin peut recommander une hystéroscopie ou un autre traitement avant transfert.",
        },
      ],
      prepInstructions: [
        "À programmer dans la première moitié du cycle (J5–J12)",
        "Prenez un antalgique en vente libre (par ex. ibuprofène) 30 à 60 minutes avant si votre médecin le recommande",
        "Évitez les rapports dans les 24 heures précédant l'examen pour limiter le risque infectieux",
        "Une courte cure d'antibiotiques peut vous être prescrite au préalable",
      ],
      risks: [
        "Crampes légères à modérées pendant et juste après l'examen",
        "Léger spotting ou écoulement aqueux pendant 1 à 2 jours",
        "Très rare : infection",
      ],
    },
  },
  sources: [
    {
      title:
        "AIUM Practice Parameter for the Performance of Sonohysterography and Hysterosalpingo-Contrast-Sonography (HyCoSy)",
      authors: "AIUM/ACOG/ACR/SRU",
      journal: "Journal of Ultrasound in Medicine",
      year: 2020,
      url: "https://www.aium.org/resources/practice-parameters/sonohysterography-and-hysterosalpingo-contrast-sonography",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(hysterosonography);

registerRelationship({
  from: hysterosonography.id,
  to: makeId("test-saline-sonogram"),
  type: "related-to",
  label: "Canonical test",
});
